import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  type CountryCode,
} from 'libphonenumber-js';

export type PhoneCountryOption = {
  code: CountryCode;
  dialCode: string;
  flag: string;
  name: string;
};

const countryCache = new Map<string, PhoneCountryOption[]>();

export function countryCodeToFlag(code: string): string {
  return code
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

export function getPhoneCountries(locale: string = 'en'): PhoneCountryOption[] {
  const cached = countryCache.get(locale);
  if (cached) return cached;

  const displayNames = new Intl.DisplayNames([locale], { type: 'region' });
  const countries = getCountries()
    .map((code) => ({
      code,
      dialCode: `+${getCountryCallingCode(code)}`,
      flag: countryCodeToFlag(code),
      name: displayNames.of(code) ?? code,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));

  countryCache.set(locale, countries);
  return countries;
}

export function defaultPhoneCountry(language: 'it' | 'en'): CountryCode {
  return language === 'it' ? 'IT' : 'US';
}

export function formatPhoneE164(country: CountryCode, localNumber: string): string {
  const digits = localNumber.replace(/\D/g, '');
  if (!digits) return '';
  return `+${getCountryCallingCode(country)}${digits}`;
}

export function isValidLocalPhoneNumber(country: CountryCode, localNumber: string): boolean {
  const full = formatPhoneE164(country, localNumber);
  if (!full) return false;
  return isValidPhoneNumber(full, country);
}
